// frontend/src/store/clientStore.js
// Store de Zustand para gestionar el estado de los clientes.

import { create } from 'zustand';
import { getAllClients as fetchAllClients, createClient as apiCreateClient,
         updateClient as apiUpdateClient, deleteClient as apiDeleteClient } from '../api/client';
import { useAuthStore } from './authStore';
import { useUIStore } from './uiStore';

/**
 * @typedef {object} ClientStoreState
 * @property {Array<object>} clients - Lista de clientes.
 * @property {boolean} isLoadingClients - Indica si se están cargando los clientes.
 * @property {object|null} selectedClient - El cliente seleccionado para ver/editar.
 * @property {function()} fetchClients - Función para cargar clientes del backend.
 * @property {function(object)} addClient - Función para añadir un nuevo cliente.
 * @property {function(string, object)} updateClient - Función para actualizar un cliente.
 * @property {function(string)} deleteClient - Función para eliminar un cliente.
 * @property {function(object|null)} setSelectedClient - Función para establecer el cliente seleccionado.
 * @property {Array<string>} opportunityNumbers - Lista de números de oportunidad únicos para filtros.
 * @property {Array<string>} accountManagers - Lista de account managers únicos para filtros.
 * @property {Array<string>} solutionSales - Lista de solution sales únicos para filtros.
 */

/**
 * @type {import('zustand').UseBoundStore<ClientStoreState>}
 */
export const useClientStore = create((set, get) => ({
  clients: [],
  isLoadingClients: false,
  selectedClient: null,
  opportunityNumbers: [],
  accountManagers: [],
  solutionSales: [],

  /**
   * @function fetchClients
   * @description Carga la lista de clientes desde el backend, con filtros opcionales.
   * También extrae los valores únicos para los filtros del dashboard.
   * @param {object} [filters={}] - Objeto con filtros (opportunityNumber, accountManager, solutionSales).
   */
  fetchClients: async (filters = {}) => { // Corregido: eliminado 'function' duplicado
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para cargar clientes.', 'error');
      return;
    }

    set({ isLoadingClients: true });
    try {
      const clients = await fetchAllClients(token, filters);
      set({ clients });

      // Extraer valores únicos para los filtros del dashboard
      const uniqueOpportunityNumbers = [...new Set(clients.map(c => c.opportunityNumber).filter(Boolean))];
      const uniqueAccountManagers = [...new Set(clients.map(c => c.accountManager).filter(Boolean))];
      const uniqueSolutionSales = [...new Set(clients.map(c => c.solutionSales).filter(Boolean))];

      set({
        opportunityNumbers: uniqueOpportunityNumbers,
        accountManagers: uniqueAccountManagers,
        solutionSales: uniqueSolutionSales,
      });

    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar clientes.', 'error');
      set({ clients: [] });
    } finally {
      set({ isLoadingClients: false });
    }
  },

  /**
   * @function addClient
   * @description Añade un nuevo cliente al backend y actualiza el store.
   * @param {object} clientData - Datos del nuevo cliente.
   */
  addClient: async (clientData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para añadir cliente.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const newClient = await apiCreateClient(clientData, token);
      set((state) => ({ clients: [newClient, ...state.clients] })); // Añadir al principio de la lista
      useUIStore.getState().showTemporaryMessage('Cliente añadido con éxito.', 'success');
      return newClient;
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al añadir cliente.', 'error');
      throw error; // Propagar el error para manejo en el formulario
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  /**
   * @function updateClient
   * @description Actualiza un cliente existente en el backend y actualiza el store.
   * @param {string} clientId - ID del cliente a actualizar.
   * @param {object} clientData - Datos actualizados del cliente.
   */
  updateClient: async (clientId, clientData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para actualizar cliente.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const updatedClient = await apiUpdateClient(clientId, clientData, token);
      set((state) => ({
        clients: state.clients.map((c) =>
          c.id === clientId ? { ...c, ...updatedClient } : c
        ),
        selectedClient: state.selectedClient?.id === clientId ? { ...state.selectedClient, ...updatedClient } : state.selectedClient,
      }));
      useUIStore.getState().showTemporaryMessage('Cliente actualizado con éxito.', 'success');
      return updatedClient;
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar cliente.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  /**
   * @function deleteClient
   * @description Elimina un cliente del backend y actualiza el store.
   * @param {string} clientId - ID del cliente a eliminar.
   */
  deleteClient: async (clientId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para eliminar cliente.', 'error');
      return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      await apiDeleteClient(clientId, token);
      set((state) => ({
        clients: state.clients.filter((c) => c.id !== clientId),
      }));
      useUIStore.getState().showTemporaryMessage('Cliente eliminado con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar cliente.', 'error');
    } finally {
      set({ isLoadingClients: false }); // Esto estaba faltando, se añade aquí
      useUIStore.getState().setIsLoading(false);
    }
  },

  /**
   * @function setSelectedClient
   * @description Establece el cliente actualmente seleccionado para visualización o edición.
   * @param {object|null} client - El objeto cliente o null para deseleccionar.
   */
  setSelectedClient: (client) => set({ selectedClient: client }),
}));
